import React, { useEffect, useState, useCallback } from "react";
import { Plus, CalendarDays, Pencil } from "lucide-react";
import api from "@/lib/api";
import { fmtEUR2, fmtDate } from "@/lib/format";
import { CARD, Label, Loader, EmptyState } from "@/components/common";
import DailyRevenueDialog from "@/components/DailyRevenueDialog";
import { Button } from "@/components/ui/button";

export default function SecretaireSaisie() {
  const [daily, setDaily] = useState([]);
  const [prats, setPrats] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, p, st] = await Promise.all([api.get("/daily-revenues"), api.get("/practitioners"), api.get("/sites")]);
      setDaily(d.data); setPrats(p.data); setSites(st.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pname = (id) => prats.find((p) => p.id === id)?.name || "Non attribué";
  const sname = (id) => sites.find((x) => x.id === id)?.name;
  const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
  const todayTotal = daily.filter((d) => d.date === todayStr).reduce((s, d) => s + (d.total || 0), 0);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setOpen(true); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-400">Encaissements journaliers</p>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Saisie du jour</h1>
        </div>
        <Button data-testid="add-daily-btn" onClick={openNew} className="rounded-full bg-[#065F46] hover:bg-[#047857]"><Plus className="mr-1.5 h-4 w-4" />Nouvelle saisie</Button>
      </div>

      <div className={`${CARD} mt-5 p-6 text-center`}>
        <Label>Total encaissé aujourd'hui</Label>
        <p data-testid="today-total" className="mt-1 font-display text-4xl font-black text-emerald-700">{fmtEUR2(todayTotal)}</p>
        <p className="mt-1 text-sm text-gray-400">{fmtDate(todayStr)}</p>
      </div>

      <div className={`${CARD} mt-5 p-4 sm:p-6`}>
        <div className="mb-2 flex items-center gap-2 text-gray-400"><CalendarDays className="h-4 w-4" /><Label>Saisies récentes</Label></div>
        {loading ? <Loader /> : daily.length === 0 ? (
          <EmptyState title="Aucune saisie" subtitle="Enregistrez le CA du jour pour un praticien." action={<Button onClick={openNew} className="rounded-full bg-[#065F46]">Nouvelle saisie</Button>} />
        ) : (
          <div className="divide-y divide-gray-50">
            {daily.slice(0, 40).map((d) => (
              <div key={d.id} className="flex items-center gap-3 py-3" data-testid={`daily-row-${d.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{pname(d.practitioner_id)}{sname(d.site_id) && <span data-testid={`daily-site-${d.id}`} className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-[#065F46]">{sname(d.site_id)}</span>}</p>
                  <p className="text-xs text-gray-400">{fmtDate(d.date)} · CB {fmtEUR2(d.cb)} · Esp. {fmtEUR2(d.especes)} · Chq {fmtEUR2(d.cheque)} · Sécu/CMU {fmtEUR2(d.secu)} · Mut. {fmtEUR2(d.mutuelle || 0)}{d.alma ? ` · Alma ${fmtEUR2(d.alma)}` : ""}</p>
                </div>
                <p className="font-display font-bold text-emerald-600">{fmtEUR2(d.total)}</p>
                <button data-testid={`edit-daily-${d.id}`} onClick={() => openEdit(d)} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100" aria-label="Modifier"><Pencil className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <DailyRevenueDialog open={open} onOpenChange={setOpen} editing={editing} onSaved={load} />
    </div>
  );
}
